import axios, { type InternalAxiosRequestConfig } from 'axios'
import posthog from 'posthog-js'

import { V1_API_URL, V1_API_URL_TESTNET, V2_API_URL } from '@src/configs/env'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('API')

// Example flags for mocking
export const MOCK_QUOTE_500 = false
export const MOCK_QUOTE_400 = false
export const MOCK_QUOTE_MALFORMED_DATA = false

export const MOCK_GAS_TOKEN_PRICES_500 = false
export const MOCK_GET_ALL_TOKENS_FROM_DB_500 = false
export const MOCK_GET_ALL_TOKENS_FROM_DB_200_NO_TOKENS = false

export const MOCK_ORDER_400 = false
export const MOCK_ORDER_200 = false

// Mock responses for testing
const mockResponses = new Map<
    string,
    {
        status: number
        data?: any
        headers?: Record<string, string>
        statusText?: string
    }
>()

// Function to set mock responses
const setMockResponse = (
    url: string,
    method: string,
    response: {
        status: number
        data?: any
        headers?: Record<string, string>
        statusText?: string
    }
) => {
    const key = `${method.toUpperCase()} ${url}`
    mockResponses.set(key, response)
}

// Conditionally set mock responses
if (MOCK_QUOTE_500) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

if (MOCK_QUOTE_400) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 400,
        headers: { 'X-Error-Code': '400' },
        statusText: 'MOCKED Bad Request',
    })
}

if (MOCK_QUOTE_MALFORMED_DATA) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 200,
        data: { srcAmount: `bad-amount - ${Math.random()}` },
        headers: {},
    })
}

if (MOCK_ORDER_400) {
    setMockResponse('/v1/orders', 'POST', {
        status: 400,
        headers: { 'X-Error-Code': '400' },
        statusText: 'MOCKED Bad request',
    })
}

if (MOCK_ORDER_200) {
    setMockResponse('/v1/orders', 'POST', {
        status: 200,
        data: { reject_order: false },
        headers: { 'X-Custom-Header': 'CustomValue' },
    })
}

// Conditionally set mock responses
if (MOCK_GAS_TOKEN_PRICES_500) {
    setMockResponse('/gas_token_prices', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

// Conditionally set mock responses
if (MOCK_GET_ALL_TOKENS_FROM_DB_500) {
    setMockResponse('/v1/tokens/get_all_tokens_from_db', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

// Conditionally set mock responses
if (MOCK_GET_ALL_TOKENS_FROM_DB_200_NO_TOKENS) {
    setMockResponse('/v1/tokens/get_all_tokens_from_db', 'POST', {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
        data: [],
    })
}

// Determine which API URL to use based on the V2_MIGRATION feature flag
export const getApiUrl = (networkType?: 'mainnet' | 'testnet', forceV1: boolean = false) => {
    // If force V1 is set to true, always use V1 API URL
    if (forceV1) {
        logger.info('Forcing V1 API URL for request', { networkType })
        return networkType === 'testnet' ? V1_API_URL_TESTNET : V1_API_URL
    }

    // If V2_MIGRATION is enabled and V2_API_URL is defined, use V2_API_URL
    if (isFeatureEnabled('V2_MIGRATION') && V2_API_URL) {
        logger.info('Using V2 API URL for mainnet requests', { networkType })
        // Return testnet URL if specified, otherwise use mainnet
        return networkType === 'testnet' ? V1_API_URL_TESTNET : V2_API_URL
    }
    // Otherwise use the standard URL based on network type
    return networkType === 'testnet' ? V1_API_URL_TESTNET : V1_API_URL
}

export const BaseURL = axios.create({
    baseURL: getApiUrl(),
})

// Helper function to add interceptors to an Axios instance
const addInterceptors = (axiosInstance: ReturnType<typeof axios.create>) => {
    // Add request logging
    axiosInstance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            logger.http(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
                headers: config.headers,
                params: config.params,
                data: config.data,
            })

            const key = `${config.method?.toUpperCase()} ${config.url}`
            if (mockResponses.has(key)) {
                const mockResponse = mockResponses.get(key)
                if (mockResponse) {
                    // Create a simulated response
                    // eslint-disable-next-line
                    config.adapter = async () => ({
                        data: mockResponse.data,
                        status: mockResponse.status,
                        statusText: mockResponse.statusText || (mockResponse.status === 200 ? 'OK' : 'Error'),
                        headers: mockResponse.headers || {},
                        config,
                    })
                }
            }
            return config
        },
        error => {
            logger.error('Request error', { error: error.message })
            posthog.capture('api_request_error', {
                url: error.config?.url,
                method: error.config?.method,
                error_message: error.message,
                error_type: 'RequestError',
            })
            return Promise.reject(error)
        }
    )

    // Add response logging
    axiosInstance.interceptors.response.use(
        response => {
            logger.http(`Response: ${response.status} ${response.config.url}`, {
                data: response.data,
            })
            return response
        },
        error => {
            const errorData = error.response?.data
            const serverMessage =
                typeof errorData === 'object' && errorData?.message
                    ? errorData.message
                    : typeof errorData === 'string'
                      ? errorData
                      : null

            logger.error('Response error', {
                url: error.config?.url,
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
                serverMessage,
            })

            // Enhance the error object with a formatted message that includes backend details
            if (serverMessage) {
                error.formattedMessage = serverMessage

                // If the error doesn't have a data property or it's not an object, initialize it
                if (!error.response) {
                    error.response = { data: {} }
                } else if (!error.response.data || typeof error.response.data !== 'object') {
                    error.response.data = {}
                }
            }

            posthog.capture('api_response_error', {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                error_message: error.message,
                server_message: serverMessage,
                error_type: 'ResponseError',
            })

            return Promise.reject(error)
        }
    )
}

// Create a separate instance for token-related API calls that always uses V1 API URL
export const TokensBaseURL = axios.create({
    baseURL: getApiUrl(undefined, true),
})

// Add interceptors to both instances
addInterceptors(BaseURL)
addInterceptors(TokensBaseURL)

export const v1TestnetApi = axios.create({ baseURL: V1_API_URL_TESTNET })

v1TestnetApi.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        logger.http(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
            headers: config.headers,
            params: config.params,
            data: config.data,
        })
        return config
    },
    error => {
        logger.error('Request error', { error: error.message })
        posthog.capture('api_testnet_request_error', {
            url: error.config?.url,
            method: error.config?.method,
            error_message: error.message,
            error_type: 'RequestError',
        })
        return Promise.reject(error)
    }
)

v1TestnetApi.interceptors.response.use(
    response => {
        logger.http(`Response: ${response.status} ${response.config.url}`, {
            data: response.data,
        })
        return response
    },
    error => {
        const errorData = error.response?.data
        const serverMessage =
            typeof errorData === 'object' && errorData?.message
                ? errorData.message
                : typeof errorData === 'string'
                  ? errorData
                  : null

        logger.error('Response error', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            serverMessage,
        })

        // Enhance the error object with a formatted message that includes backend details
        if (serverMessage) {
            error.formattedMessage = serverMessage

            // If the error doesn't have a data property or it's not an object, initialize it
            if (!error.response) {
                error.response = { data: {} }
            } else if (!error.response.data || typeof error.response.data !== 'object') {
                error.response.data = {}
            }

            // Ensure the server message is always available in the response data
            error.response.data.serverMessage = serverMessage
        }

        posthog.capture('api_testnet_response_error', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            error_message: error.message,
            server_message: serverMessage,
            error_data: error.response?.data,
            error_type: error.response?.status ? `HTTP${error.response.status}` : 'NetworkError',
        })
        return Promise.reject(error)
    }
)
